/**
 * Unified loading prompts for BrandedLoader and Splash.
 * Uses sessionStorage to rotate through prompts sequentially.
 */

const PROMPTS = [
  "Another day of choosing **happiness**. Let's go. ✦",
  "What happened today that was **good**?",
  "What am I taking for granted that I can be **thankful** for?",
  "Which **people** in my life am I grateful for?",
  "What is the last book, movie, or clip I really **appreciated**?",
  "What am I most **looking forward** to this week, month, and year?",
  "What is the **kindest** thing someone has said or done lately?",
  "What made you **smile** today?",
  "Think of one thing you're **grateful** for right now...",
  "Name something **beautiful** you noticed today...",
  "What's one thing going **right** in your life right now?",
  "Your future self will **thank** you for this.",
  "5 minutes. **Real change**. You've got this.",
  "What's one **win** from today?",
  "Speak your **thanks** and watch them multiply. ✦",
  "What felt like a quiet **gift** today?",
  "Who made you **smile** recently?",
  "Your **happiness formula** is waiting to be revealed. ✦",
  "What do your **happiest days** have in common?",
  "You're **rewiring your brain** right now. ✦",
  "One more entry, **one step closer**. ✦",
  "Let your thanks be spoken. That's where the **magic** is. ✦",
  "Say **thanks** to someone you love today. ✦",
];

const STORAGE_KEY = "thnx_prompt_idx";

export interface ParsedPrompt {
  before: string;
  highlight: string;
  after: string;
}

function parsePrompt(raw: string): ParsedPrompt {
  const match = raw.match(/^(.*?)\*\*(.+?)\*\*(.*)$/);
  if (!match) return { before: raw, highlight: "", after: "" };
  return { before: match[1], highlight: match[2], after: match[3] };
}

export function getNextPrompt(): ParsedPrompt {
  const index = parseInt(sessionStorage.getItem(STORAGE_KEY) || "0", 10);
  const prompt = PROMPTS[index % PROMPTS.length];
  sessionStorage.setItem(STORAGE_KEY, String(index + 1));
  return parsePrompt(prompt);
}

// In-memory cache so every loader that mounts for the same `key` shares the
// same prompt. When the key changes (e.g. the user navigates to a different
// route), we advance to the next prompt via getNextPrompt(). Without this,
// a single navigation can mount 2–3 sequential BrandedLoaders (Suspense
// fallback → ProtectedRoute → page-level) that each pick a different prompt.
let cachedKey: string | null = null;
let cachedPrompt: ParsedPrompt | null = null;

export function getStableLoaderPrompt(key: string): ParsedPrompt {
  if (key !== cachedKey || !cachedPrompt) {
    cachedKey = key;
    cachedPrompt = getNextPrompt();
  }
  return cachedPrompt;
}

// Exposed for tests only.
export function _resetStableLoaderPromptForTests(): void {
  cachedKey = null;
  cachedPrompt = null;
}
